import React, { useState } from 'react';
import { CheckCircle2, Loader2, Mail, UserRound } from 'lucide-react';
import { CLOUD_FUNCTIONS_BASE_URL } from '../../auth/firebaseConfig';

type MarketingUserType = 'renter' | 'landlord' | 'agent' | 'other';

interface PseoEmailCaptureProps {
  pageType: string;
  pageSlug: string;
  locationName?: string;
  contextHeadline: string;
  contextReason: string;
  ctaLabel?: string;
}

const USER_TYPE_OPTIONS: Array<{ value: MarketingUserType; label: string }> = [
  { value: 'renter', label: 'Renter' },
  { value: 'landlord', label: 'Landlord' },
  { value: 'agent', label: 'Agent' },
  { value: 'other', label: 'Other' },
];

const PAGE_VARIANTS: Record<string, { ctaLabel: string; urgencyHint: string }> = {
  'rent-affordability': {
    ctaLabel: 'Get Early Affordability Alerts',
    urgencyHint: 'Affordability windows can tighten quickly when rents jump.',
  },
  'new-vs-old': {
    ctaLabel: 'Get New vs Old Market Alerts',
    urgencyHint: 'Grants and financing gaps can shift faster than expected.',
  },
  'schools-around': {
    ctaLabel: 'Get School-Area Property Alerts',
    urgencyHint: 'Family-demand areas often move fast once new stock appears.',
  },
  'eviction-rights': {
    ctaLabel: 'Get Rights & Market Updates',
    urgencyHint: 'Compliance and notice-rule updates are time-sensitive.',
  },
  'self-build': {
    ctaLabel: 'Get Self-Build Cost Alerts',
    urgencyHint: 'Build-cost and planning-risk changes can affect timelines quickly.',
  },
  'housing-reports': {
    ctaLabel: 'Get Housing Report Alerts',
    urgencyHint: 'County-level demand signals can change before broad headlines.',
  },
  'landlord-insights': {
    ctaLabel: 'Get Landlord Intelligence Alerts',
    urgencyHint: 'Yield and demand signals can move before listing competition catches up.',
  },
  'salary-after-tax': {
    ctaLabel: 'Get Salary & Rent Pressure Alerts',
    urgencyHint: 'Take-home and rent pressure context evolves through the year.',
  },
  'good-salary': {
    ctaLabel: 'Get Salary Reality Alerts',
    urgencyHint: 'Local salary-to-rent reality can shift with market pressure.',
  },
  'cost-of-living': {
    ctaLabel: 'Get Cost Pressure Alerts',
    urgencyHint: 'County cost pressure can rise before households adjust.',
  },
  'city-comparisons': {
    ctaLabel: 'Get City Comparison Alerts',
    urgencyHint: 'Relocation trade-offs can change quickly with rent and commute shifts.',
  },
  'rent-increase-notice': {
    ctaLabel: 'Get RPZ Rule & Market Alerts',
    urgencyHint: 'Notice and compliance timing can materially change outcomes.',
  },
  'dublin-vs-commuter': {
    ctaLabel: 'Get Commuter Trade-Off Alerts',
    urgencyHint: 'Commuter-value opportunities close quickly as demand rotates.',
  },
  'living-in-area': {
    ctaLabel: 'Get Area Trend Alerts',
    urgencyHint: 'Neighborhood demand and pricing momentum can shift rapidly.',
  },
};

const PseoEmailCapture: React.FC<PseoEmailCaptureProps> = ({
  pageType,
  pageSlug,
  locationName,
  contextHeadline,
  contextReason,
  ctaLabel,
}) => {
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState<MarketingUserType>('renter');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const variant = PAGE_VARIANTS[pageType] || {
    ctaLabel: 'Get Early Market Updates',
    urgencyHint: 'Early subscribers usually see market shifts first.',
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!/\S+@\S+\.\S+/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    if (!consent) {
      setError('Consent is required before subscribing.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/subscribeEmailMarketing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          userType,
          consent,
          pageType,
          pageSlug,
          locationName: locationName || '',
          contextReason,
          sourcePath: window.location.pathname,
          sourceUrl: window.location.href,
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        setError(result.error || 'Failed to subscribe. Please try again.');
        return;
      }

      if (result.alreadyVerified) {
        setSuccessMessage(
          result.message ||
            'You are already verified. We have updated your RentSmart market update preferences.'
        );
      } else {
        setSuccessMessage(
          result.message ||
            'Check your inbox to verify your email before receiving Irish property market updates.'
        );
      }

      setEmail('');
      setConsent(false);
      setUserType('renter');
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Failed to subscribe. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="rounded-[2rem] border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-950 p-6 md:p-8 text-white shadow-xl">
      <div className="flex items-center gap-2 text-emerald-300 text-xs font-black uppercase tracking-widest mb-3">
        <Mail className="h-4 w-4" /> Email Updates
      </div>
      <h2 className="text-2xl md:text-3xl font-black tracking-tight mb-2">{contextHeadline}</h2>
      <p className="text-slate-200 text-sm leading-relaxed mb-5">{contextReason}</p>
      <p className="text-xs font-semibold text-slate-300 mb-2">
        You will receive property news and updates in the Irish market only after email verification.
      </p>
      <p className="text-xs font-semibold text-emerald-200/90 mb-6">
        {variant.urgencyHint}
      </p>

      {successMessage ? (
        <div className="rounded-2xl border border-emerald-300/40 bg-emerald-400/10 p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 mt-0.5 text-emerald-300" />
          <p className="text-sm font-semibold text-emerald-100">{successMessage}</p>
        </div>
      ) : null}

      {error ? (
        <div className="rounded-2xl border border-rose-300/40 bg-rose-400/10 p-4 mt-4">
          <p className="text-sm font-semibold text-rose-100">{error}</p>
        </div>
      ) : null}

      <form className="space-y-4 mt-5" onSubmit={handleSubmit}>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label
              htmlFor={`email-capture-${pageSlug}`}
              className="block text-[10px] font-black uppercase tracking-wider text-slate-300 mb-1.5"
            >
              Email Address
            </label>
            <input
              id={`email-capture-${pageSlug}`}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full rounded-xl border border-white/20 bg-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-300"
              autoComplete="email"
              required
            />
          </div>
          <div>
            <label
              htmlFor={`usertype-capture-${pageSlug}`}
              className="text-[10px] font-black uppercase tracking-wider text-slate-300 mb-1.5 inline-flex items-center gap-1"
            >
              <UserRound className="h-3.5 w-3.5" /> I Am A
            </label>
            <select
              id={`usertype-capture-${pageSlug}`}
              value={userType}
              onChange={(e) => setUserType(e.target.value as MarketingUserType)}
              className="w-full rounded-xl border border-white/20 bg-white/10 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-300"
            >
              {USER_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value} className="text-slate-900">
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label className="flex items-start gap-3 rounded-xl border border-white/15 bg-white/5 px-3 py-3">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300"
          />
          <span className="text-xs text-slate-200 leading-relaxed">
            I consent to receive RentSmart email updates, market insights, and property news
            relevant to the Irish market. I understand that my email must be verified first and
            that timely updates can affect rental and buying decisions.
          </span>
        </label>

        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-black text-emerald-950 hover:bg-emerald-400 disabled:opacity-70 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            ctaLabel || variant.ctaLabel
          )}
        </button>
      </form>
    </section>
  );
};

export default PseoEmailCapture;
